import React, { useState, useRef, useEffect } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Conversation } from '@elevenlabs/client';

interface VoiceCallWidgetProps {
  agentId: string;
  className?: string;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';
type AgentMode = 'idle' | 'listening' | 'thinking' | 'speaking';

const VoiceCallWidget: React.FC<VoiceCallWidgetProps> = ({
  agentId,
  className = ''
}) => {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [mode, setMode] = useState<AgentMode>('idle');
  const [transcript, setTranscript] = useState<string[]>([]);
  const [isMuted, setIsMuted] = useState(false);
  const conversationRef = useRef<any>(null);

  const startConversation = async () => {
    try {
      setStatus('connecting');

      const conversation = await Conversation.startSession({
        agentId: agentId,
        connectionType: 'webrtc',
        onMessage: (message) => {
          // Handle transcript messages
          if (message.type === 'agent_response' || message.type === 'user_transcript') {
            setTranscript(prev => [...prev, `${message.role}: ${message.message}`]);
          }
        },
        onStatusChange: (newStatus) => {
          setStatus(newStatus.status as ConnectionStatus);
        },
        onModeChange: (newMode) => {
          // Map ElevenLabs mode to our mode
          if (newMode.mode === 'listening') setMode('listening');
          else if (newMode.mode === 'thinking') setMode('thinking');
          else if (newMode.mode === 'speaking') setMode('speaking');
          else setMode('idle');
        },
        onError: (error) => {
          console.error('Conversation error:', error);
          setStatus('disconnected');
        }
      });

      conversationRef.current = conversation;
      setStatus('connected');
    } catch (error) {
      console.error('Failed to start conversation:', error);
      setStatus('disconnected');
    }
  };

  const endConversation = async () => {
    if (conversationRef.current) {
      await conversationRef.current.endSession();
      conversationRef.current = null;
      setStatus('disconnected');
      setMode('idle');
      setTranscript([]);
    }
  };

  const toggleMute = () => {
    if (conversationRef.current) {
      const newMutedState = !isMuted;
      conversationRef.current.setVolume(newMutedState ? 0 : 1);
      setIsMuted(newMutedState);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (conversationRef.current) {
        conversationRef.current.endSession();
      }
    };
  }, []);

  const getStatusText = () => {
    if (status === 'connecting') return 'Connecting...';
    if (status === 'disconnected') return 'Click to start talking';

    switch (mode) {
      case 'listening': return 'Listening...';
      case 'thinking': return 'Thinking...';
      case 'speaking': return 'Speaking...';
      default: return 'Connected';
    }
  };

  const getStatusColor = () => {
    if (status === 'disconnected') return 'bg-gray-200 text-gray-700';
    if (status === 'connecting') return 'bg-secondary/20 text-dark';

    switch (mode) {
      case 'listening': return 'bg-primary/20 text-dark';
      case 'thinking': return 'bg-accent/20 text-dark';
      case 'speaking': return 'bg-primary/30 text-dark';
      default: return 'bg-primary/20 text-dark';
    }
  };

  return (
    <div className={`bg-white rounded-xl border-2 border-gray-200 p-8 ${className}`}>
      {/* Visual Status Indicator */}
      <div className="flex flex-col items-center justify-center mb-8">
        {/* Animated Orb */}
        <motion.div
          className="relative mb-6"
          animate={{
            scale: mode === 'speaking' || mode === 'listening' ? [1, 1.1, 1] : 1,
          }}
          transition={{
            duration: 1.5,
            repeat: mode === 'speaking' || mode === 'listening' ? Infinity : 0,
            ease: "easeInOut"
          }}
        >
          {/* Outer glow ring */}
          <motion.div
            className={`absolute inset-0 rounded-full blur-2xl ${
              status === 'connected' ? 'bg-primary/30' : 'bg-gray-300/30'
            }`}
            animate={{
              scale: mode === 'speaking' ? [1, 1.5, 1] : 1,
              opacity: mode === 'speaking' ? [0.3, 0.6, 0.3] : 0.3,
            }}
            transition={{
              duration: 2,
              repeat: mode === 'speaking' ? Infinity : 0,
            }}
          />

          {/* Main orb */}
          <div className={`relative w-32 h-32 rounded-full flex items-center justify-center transition-colors duration-300 ${
            status === 'connected'
              ? 'bg-gradient-to-br from-primary via-primary to-[#3a9770]'
              : 'bg-gradient-to-br from-gray-300 to-gray-500'
          }`}>
            {status === 'connecting' ? (
              <Loader2 className="w-12 h-12 text-white animate-spin" />
            ) : status === 'connected' ? (
              mode === 'listening' ? (
                <Mic className="w-12 h-12 text-white animate-pulse" />
              ) : (
                <Phone className="w-12 h-12 text-white" />
              )
            ) : (
              <Phone className="w-12 h-12 text-white" />
            )}
          </div>
        </motion.div>

        {/* Status Text */}
        <div className={`px-4 py-2 rounded-full text-sm font-medium ${getStatusColor()}`}>
          {getStatusText()}
        </div>
      </div>

      {/* Control Buttons */}
      <div className="flex gap-4 justify-center mb-6">
        {status === 'disconnected' ? (
          <button
            onClick={startConversation}
            className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-semibold text-lg transition shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Start Conversation
          </button>
        ) : (
          <>
            <button
              onClick={toggleMute}
              className={`${
                isMuted ? 'bg-red-600 hover:bg-red-700' : 'bg-dark hover:bg-dark/90'
              } text-white px-6 py-3 rounded-full font-semibold transition shadow-lg flex items-center gap-2`}
            >
              {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              {isMuted ? 'Unmute' : 'Mute'}
            </button>

            <button
              onClick={endConversation}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-semibold transition shadow-lg flex items-center gap-2"
            >
              <PhoneOff className="w-5 h-5" />
              End Call
            </button>
          </>
        )}
      </div>

      {/* Transcript Display (Optional) */}
      {transcript.length > 0 && (
        <div className="mt-6 max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-4">
          <p className="text-xs font-semibold text-gray-500 mb-2">Conversation:</p>
          {transcript.slice(-5).map((line, index) => (
            <p key={index} className="text-sm text-gray-700 mb-1">
              {line}
            </p>
          ))}
        </div>
      )}

      {/* Help Text */}
      <p className="text-sm text-gray-600 text-center mt-4">
        {status === 'disconnected'
          ? 'Click above to start a live voice conversation'
          : 'Speak naturally - the AI will respond in real-time'
        }
      </p>
    </div>
  );
};

export default VoiceCallWidget;
